import Modal, { ModalBody, ModalHeader, Sheets } from '@ingka/modal';
import SSRIcon from '@ingka/ssr-icon';
import arrowDownToLineIcon from '@ingka/ssr-icon/paths/arrow-down-to-line';
import chatIcon from '@ingka/ssr-icon/paths/chat';
import linkOutIcon from '@ingka/ssr-icon/paths/link-out';
import trashCanIcon from '@ingka/ssr-icon/paths/trash-can';
import type { TranslateFunction } from '@repo/phrase';

import styles from './attachment-edit-sheet.module.scss';

interface EditAction {
    id: string;
    translationKey: string;
    icon: (prefix?: string) => Array<React.SVGProps<SVGElement>>;
    onClick: () => void;
}

interface AttachmentEditSheetProps {
    visible: boolean;
    onClose: () => void;
    onOpenInNewTab: () => void;
    onComment?: () => void;
    onDownload: () => void;
    onDelete?: () => void;
    translate: TranslateFunction;
}

export const AttachmentEditSheet = ({
    visible,
    onClose,
    onOpenInNewTab,
    onComment,
    onDownload,
    onDelete,
    translate,
}: AttachmentEditSheetProps) => {
    const actions: EditAction[] = [
        {
            id: 'open-in-new-tab',
            translationKey: 'attachments_edit_open_tab',
            icon: linkOutIcon,
            onClick: onOpenInNewTab,
        },
        ...(onComment
            ? [
                  {
                      id: 'comment',
                      translationKey: 'attachments_edit_comment',
                      icon: chatIcon,
                      onClick: onComment,
                  },
              ]
            : []),
        {
            id: 'download',
            translationKey: 'attachments_edit_download',
            icon: arrowDownToLineIcon,
            onClick: onDownload,
        },
        ...(onDelete
            ? [
                  {
                      id: 'delete',
                      translationKey: 'attachments_edit_remove',
                      icon: trashCanIcon,
                      onClick: onDelete,
                  },
              ]
            : []),
    ];

    return (
        <Modal
            handleCloseBtn={onClose}
            visible={visible}
            focusLockProps={{ autoFocusReturn: false, autoFocus: true }}
            data-testid="attachment-edit-sheet"
        >
            <Sheets header={<ModalHeader />} footer={null} aria-label={translate('attachments_edit_sheet_label')}>
                <ModalBody>
                    <ul className={styles.action_list} data-testid="attachment-edit-actions">
                        {actions.map((action) => (
                            <li key={action.id} className={styles.action_list_item}>
                                <button
                                    type="button"
                                    className={styles.action_button}
                                    onClick={action.onClick}
                                    data-testid={`attachment-action-${action.id}`}
                                >
                                    <span>{translate(action.translationKey)}</span>
                                    <SSRIcon paths={action.icon} />
                                </button>
                            </li>
                        ))}
                    </ul>
                </ModalBody>
            </Sheets>
        </Modal>
    );
};
